"use client";

import { Network, truncateAddress } from "@aptos-labs/ts-sdk";
import { IconX } from "@decibeltrade/react-icons";
import type { FunPaymentItem } from "@decibeltrade/utils";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { isEvmChain } from "@/constants/chains";
import { ChainName } from "@/contexts/decibel-wallet-provider";
import { useSubaccountsWithPrimary } from "@/hooks/use-subaccounts-with-primary";
import { addressesEqual } from "@/utils/address";
import { getBridgeTxUrl } from "@/utils/cctp/redemption";
import type { TransferSpeed } from "@/utils/cctp/v2/fees";
import type { IrisDelayReason } from "@/utils/cctp/v2/iris";
import type { CCTPVersion } from "@/utils/cctp/version";
import { NumberFormatter } from "@/utils/number-formatter";
import { getExplorerUrl, getSourceChainExplorerUrl } from "@/utils/url";
import { getCancelReasonI18nKey } from "@/utils/withdraw-queue-events";

import { Clickable } from "../clickable";
import { CollapsibleIconButton } from "../collapsible-icon-button";
import { isSentinelQueuedXchainWithdraw, isWithdrawTransfer } from "./transfer-arms";
import { deriveCapabilities } from "./transfer-capabilities";
import {
  progressContainerStyles,
  StepIcon,
  type StepIconVariant,
  StepRow,
  stepSubtitleStyles,
  transferCardStyles,
  TxLink,
} from "./transfer-step-parts";
import { describeTimeline, type TimelineRow } from "./transfer-timeline";

export type TransferStep =
  | "initiated"
  | "withdrawal-queued"
  | "withdrawal-cancelled"
  | "confirming"
  | "bridging"
  | "claimable"
  | "available"
  | "completed"
  /** Terminal failure of an inbound transfer — the funds were refunded or never swapped. */
  | "transfer-failed";

export type TransferStatus = "pending" | "in-progress" | "complete";

// Re-exported from their own pure module so `transfer-capabilities.ts` and `transfer-timeline.ts`
// can classify an arm without importing this file at runtime. Existing consumers import from here.
export { isWithdrawTransfer };
export type { WithdrawType } from "./transfer-arms";

export type TransferProgressData = {
  id: string;
  amount: number;
  destinationAddress: string;
  currentStep: TransferStep;
  timeRemaining?: string;
  txHash?: string;
  destinationChain: ChainName;
  /** Cached USDC balance when transfer was added (Aptos). Used for balance polling so refresh keeps correct baseline. */
  initialBalance?: number;
  /** Estimated bridge time in seconds from the Wormhole quote, used to display bridging time range */
  estimatedBridgeTimeSeconds?: number;
  /** Transaction hash on the destination chain (set when claim completes or polling detects it) */
  destinationTxHash?: string;
  /** Set once the confirmed transfer has been POSTed to /api/v1/transfers, so the reporting effect fires exactly once. */
  reportedToWarehouse?: boolean;
} & (
  | {
      type: "cctp-deposit";
      sourceChain: ChainName;
      sourceTxHash: string;
      subaccountToDepositTo: string;
      attestation?: string;
      messageBytes?: string;
      /** Missing on every card stored before v2, which are all v1. */
      cctpVersion?: CCTPVersion;
      /** A v2 burn's speed. Missing means Standard, which is all v2 sent before the picker. */
      speed?: TransferSpeed;
      /** Set while Circle renews an expired Fast attestation: the expired copy's expirationBlock. */
      renewedFromExpirationBlock?: string;
      /** Why Circle is holding a Fast transfer to Standard finality. */
      delayReason?: IrisDelayReason;
    }
  | {
      type: "mesh-deposit";
      sourceChain: ChainName;
      subaccountToDepositTo: string;
    }
  | {
      type: "xchain-deposit";
      /**
       * The network the funds were delivered through, not the one the user sent from.
       *
       * A delivery reports only the sweep into the destination asset — many chains fund one
       * Intents account, and nothing in the response says which of them paid for this one. NEAR
       * is the honest answer and the one the deposit copy already gives ("Deposits are managed by
       * NEAR"). Deliberately not `ChainName`: this is server-shaped config, not a build literal.
       */
      sourceChain: string;
      subaccountToDepositTo: string;
      /**
       * The card's identity: the destination-chain transaction hash of one delivery.
       *
       * Not the deposit address, which now identifies a long-lived Intents account rather than a
       * send — keying on it would collapse every deposit a user ever makes into one card.
       */
      deliveryId: string;
      /** Which collector delivered this, e.g. "usdc". */
      destinationAssetKey: string;
      /**
       * What actually landed on Aptos, as the server reported it with the delivery.
       *
       * All four are optional only for cards persisted before the server carried them; every new
       * card has them, because a delivery describes the asset that arrived. A card without them
       * formats amounts as USDC (which every enabled route delivers) and offers no sweep.
       */
      destinationSymbol?: string;
      destinationFaAddress?: string;
      destinationDecimals?: number;
      /** Whether the delivered asset can be moved into cross margin at all. */
      sweepable?: boolean;
      /**
       * The exact decimal figure the server reported, which `amount` is only a float view of.
       *
       * The move into collateral takes a `u64`, and deriving one from the float loses a base
       * unit on most fractional amounts — see `actions/sweep-amount.ts`. Optional because cards
       * persisted to localStorage before this field existed do not carry it.
       */
      amountFormatted?: string;
      /**
       * What 1Click actually charged this delivery, in the destination asset's own units.
       *
       * Only known once the ANY_INPUT collector's sweep has settled — present on every delivery
       * this card is ever created from (a delivery comes into existence already settled), and
       * absent only on a card persisted before the server carried it. Never derived client-side by
       * diffing an input and output amount; `amount`/`amountFormatted` are already net of it.
       */
      feeFormatted?: string;
      /** USD value of `feeFormatted` at delivery, when the server supplied one. */
      feeUsd?: string;
    }
  | {
      type: "cctp-withdraw";
      sourceChain: ChainName;
      sourceTxHash: string;
      accountToWithdrawTo: string;
      // Decibel subaccount the funds are withdrawn FROM (joins to on-chain trading
      // data). Distinct from `accountToWithdrawTo`, which is the external recipient.
      subaccountAddress: string;
      attestation?: string;
      messageBytes?: string;
      /** Missing on every card stored before v2, which are all v1. */
      cctpVersion?: CCTPVersion;
    }
  | {
      type: "queued-withdraw";
      sourceChain: ChainName;
      requestId: string;
      userAddress: string;
      // Decibel subaccount the funds are withdrawn FROM (joins to on-chain trading
      // data). Distinct from `userAddress`, which is the owner Aptos wallet.
      subaccountAddress: string;
      processedAmount?: number;
      cancelReason?: string;
    }
  | {
      type: "queued-xchain-withdraw";
      sourceChain: ChainName;
      requestId: string;
      userAddress: string;
      accountToWithdrawTo: string;
      // Decibel subaccount the funds are withdrawn FROM (joins to on-chain trading
      // data). Distinct from `userAddress` (owner wallet) and `accountToWithdrawTo`
      // (external recipient).
      subaccountAddress: string;
      withdrawAmount: number;
      processedAmount?: number;
      cancelReason?: string;
      /** Server time a "Continue Bridge" burn may have landed without us seeing it. */
      burnUnconfirmedAt?: number;
    }
  | {
      type: "xchain-withdraw";
      sourceChain: ChainName;
      /**
       * Display name of the chain the funds are headed for, straight from the server allowlist.
       * Deliberately not `ChainName`: Bitcoin is not one of those, and which chains are offered is
       * server config. The base `destinationChain` names the chain our own transaction settles on,
       * which is Aptos — the leg beyond it is 1Click's, and this card links to no explorer for it.
       */
      destinationChainLabel: string;
      /**
       * 1Click deposit address the funds were sent to, and the card's identity: the server reports
       * one status per address, and a withdrawal quote mints a fresh one per send.
       */
      depositAddress: string;
      routeId: string;
      /** What the user receives, and what the card formats amounts as. */
      symbol: string;
      decimals: number;
      /** The external address the user typed. Distinct from `subaccountAddress`, the source. */
      accountToWithdrawTo: string;
      subaccountAddress: string;
      /** The exact decimal figure, for the same `u64` reason `xchain-deposit` carries one. */
      amountFormatted?: string;
      /** Refund reason or raw upstream status, shown when the transfer terminates as failed. */
      failureReason?: string;
      /**
       * What 1Click quoted for this withdrawal, in the destination asset's own units.
       *
       * Fixed by the quote rather than the live status, so it is known from the moment the card is
       * created — unlike a deposit's fee, which only exists after settlement. Never derived
       * client-side by diffing amounts; `amountFormatted` is already net of it.
       */
      feeFormatted?: string;
    }
  | {
      type: "fun-deposit";
      /** Constant "Fun"; the card names no origin chain. */
      sourceChain: string;
      /** The server item's current id. The card matches on it or any of `aliases`. */
      paymentId: string;
      aliases: string[];
      /** Rendered as-is; `currentStep` only mirrors it, except the client-owned `completed`. */
      projection: FunPaymentItem;
      subaccountToDepositTo: string;
      /** Copied from `projection.delivery` so the deposit action reads the xchain fields. */
      deliveryId?: string;
      destinationAssetKey?: string;
      destinationSymbol?: string;
      destinationFaAddress?: string;
      destinationDecimals?: number;
      sweepable?: boolean;
      amountFormatted?: string;
      feeFormatted?: string;
      feeUsd?: string;
    }
);

interface TransferProgressCardProps {
  transfer: TransferProgressData;
  onClose?: (transferId: string) => void;
  removeTransfer: (transferId: string) => void;
  /** Render a custom action (e.g. "Confirm Deposit") when transfer is complete. When omitted, a dismiss button is shown instead. */
  renderActionButton?: (props: { expanded: boolean }) => ReactNode;
  /** Whether a server-side auto-claim is in progress (changes the claimable subtitle) */
  isAutoClaiming?: boolean;
  /** Override card styles */
  className?: string;
  footer?: ReactNode;
}

/** Returns a static confirmation time estimate based on the source chain's finality. */
function getConfirmationTimeEstimate(sourceChain: string): string {
  const chain = sourceChain.toLowerCase();
  if (chain === "solana") return "~30 sec";
  if (chain === "aptos" || chain === "sui") return "<1 min";
  // EVM chains (Ethereum, Arbitrum, Base, etc.)
  return "~2 min";
}

// Why Circle is holding a Fast transfer to Standard finality, as the bridging row explains it.
const FAST_DELAY_SUBTITLE = {
  insufficient_fee: "subtitles.fastDelayedFee",
  amount_above_max: "subtitles.fastDelayedAmount",
  insufficient_allowance_available: "subtitles.fastDelayedAllowance",
} as const satisfies Record<IrisDelayReason, string>;

/** Formats an estimated bridge time (seconds) into a user-friendly range like "~10-15 min". */
function formatBridgeTimeRange(seconds: number): string {
  // A Fast deposit quotes seconds; rounding it up to "~1-5 min" contradicts the picker.
  if (seconds < 60) return "<1 min";
  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 3) return "~1-5 min";
  if (minutes <= 7) return "~3-7 min";
  const lower = Math.floor(minutes / 5) * 5;
  const upper = lower + 5;
  return `~${lower}-${upper} min`;
}

export function TransferProgressCard({
  transfer,
  removeTransfer,
  renderActionButton,
  isAutoClaiming,
  className,
  footer,
}: TransferProgressCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [highlightElapsed, setHighlightElapsed] = useState(false);
  const t = useTranslations("transferProgress");
  const tGeneral = useTranslations("general");

  const rows = describeTimeline(transfer);

  const isQueuedTransfer =
    transfer.type === "queued-withdraw" || transfer.type === "queued-xchain-withdraw";

  const isCancelled = transfer.currentStep === "withdrawal-cancelled";

  const isFailed = transfer.currentStep === "transfer-failed";

  const { isTerminal: isComplete, isDismissible, settlement } = deriveCapabilities(transfer);

  const highlighTransfer =
    transfer.currentStep === "claimable" || transfer.currentStep === "confirming";

  const totalSteps = rows.length;
  const completedSteps = rows.filter((row) => row.status === "complete").length;

  // Highlight for 0.5 seconds when transfer completes
  useEffect(() => {
    if (highlighTransfer) {
      const timer = setTimeout(() => {
        setHighlightElapsed(true);
      }, 500);

      return () => {
        setHighlightElapsed(false);
        clearTimeout(timer);
      };
    }
  }, [highlighTransfer]);
  const isHighlighted = highlighTransfer && !highlightElapsed;

  const { subaccounts } = useSubaccountsWithPrimary();

  const destinationAccountAddress =
    transfer.type === "cctp-withdraw" ||
    transfer.type === "queued-xchain-withdraw" ||
    transfer.type === "xchain-withdraw"
      ? transfer.accountToWithdrawTo
      : transfer.type === "queued-withdraw"
        ? transfer.userAddress
        : transfer.subaccountToDepositTo;

  const subaccountLabel =
    transfer.type === "cctp-deposit" || transfer.type === "xchain-deposit"
      ? (subaccounts.find((subaccount) =>
          addressesEqual(subaccount.subaccount_address, transfer.subaccountToDepositTo),
        )?.custom_label ?? truncateAddress(transfer.subaccountToDepositTo))
      : undefined;

  const isCctp = transfer.type === "cctp-deposit" || transfer.type === "cctp-withdraw";
  const sourceTxHash = isCctp ? transfer.sourceTxHash : undefined;
  // Wormhole's testnet CCTP only has the Sepolia chains, so a v1 card's Ethereum/Arbitrum/Base hash
  // is mainnet even on testnet. v1 Solana and Sui follow the app's network.
  const chainNetwork = (chain: string): Network | undefined =>
    isCctp && transfer.cctpVersion !== 2 && isEvmChain(chain) ? Network.MAINNET : undefined;

  const getSourceExplorerUrl = (): string | null => {
    if (!sourceTxHash) return null;
    // For withdrawals the source chain is Aptos
    if (transfer.sourceChain === "Aptos") return getExplorerUrl(sourceTxHash);
    return getSourceChainExplorerUrl(
      transfer.sourceChain,
      sourceTxHash,
      chainNetwork(transfer.sourceChain),
    );
  };

  const getBridgeExplorerUrl = (): string | null => {
    if (!sourceTxHash) return null;
    return (
      getBridgeTxUrl(sourceTxHash, isCctp ? transfer.cctpVersion : undefined) ??
      getSourceExplorerUrl()
    );
  };

  const getDestinationExplorerUrl = (): string | null => {
    if (!transfer.destinationTxHash) return null;
    // For deposits the destination is Aptos
    if (transfer.destinationChain === "Aptos") return getExplorerUrl(transfer.destinationTxHash);
    return getSourceChainExplorerUrl(
      transfer.destinationChain,
      transfer.destinationTxHash,
      chainNetwork(transfer.destinationChain),
    );
  };

  const renderTxLink = (url: string | null): ReactNode => <TxLink url={url} />;

  /**
   * The asset this card is about.
   *
   * The two cross-chain types carry whatever their route moves; every other transfer type here is
   * USDC. The deposit fallback covers a card added while the server allowlist was unavailable,
   * which is also the only case where it can be wrong — every enabled route delivers USDC today.
   * A withdrawal always knows its asset, because the route it was quoted from named it.
   */
  const assetSymbol =
    transfer.type === "xchain-deposit"
      ? (transfer.destinationSymbol ?? "USDC")
      : transfer.type === "xchain-withdraw"
        ? transfer.symbol
        : "USDC";
  const assetDecimals =
    transfer.type === "xchain-deposit"
      ? transfer.destinationDecimals
      : transfer.type === "xchain-withdraw"
        ? transfer.decimals
        : undefined;

  /**
   * The amount with no unit attached.
   *
   * USDC's fixed two decimals are wrong for anything else — 0.0002 BTC would round to 0.00 — so a
   * non-USDC asset is formatted at its own precision.
   */
  const formatAmount = (value: number): string =>
    assetSymbol === "USDC"
      ? NumberFormatter.usdc(value, { symbol: false })
      : NumberFormatter.size(value, { szDecimals: assetDecimals });

  /**
   * The bridge fee row's contents, or nothing to render.
   *
   * Only `xchain-deposit` and `xchain-withdraw` ever carry a fee; every other arm's type has no
   * such field, so this is `undefined` for them without any per-type branching here. A deposit's
   * fee only exists after settlement; a withdrawal's is fixed at quote time and known from the
   * card's creation — either way, this renders the exact string the server reported, never a
   * value derived by subtracting `amount` from something else.
   */
  const feeFormatted =
    transfer.type === "xchain-deposit" || transfer.type === "xchain-withdraw"
      ? transfer.feeFormatted
      : undefined;
  const feeUsd = transfer.type === "xchain-deposit" ? transfer.feeUsd : undefined;

  /** The amount as a title says it: "$1.00" for USDC, "0.0002 BTC" for anything else. */
  const formatAmountWithUnit = (value: number): string =>
    assetSymbol === "USDC" ? NumberFormatter.usdc(value) : `${formatAmount(value)} ${assetSymbol}`;

  const getStepTitle = (row: TimelineRow): ReactNode => {
    const hasStepFinished = row.status === "complete";

    switch (row.step) {
      case "initiated":
        return t("steps.initiated", {
          amount: formatAmount(transfer.amount),
          asset: assetSymbol,
        });
      case "withdrawal-queued":
        return hasStepFinished ? t("steps.withdrawalQueueProcessed") : t("steps.withdrawalQueued");
      case "withdrawal-cancelled":
        return t("steps.withdrawalCancelled");
      case "transfer-failed":
        return t("steps.transferFailed");
      case "confirming": {
        // A cross-chain deposit has no origin worth naming: many chains fund the one Intents
        // account and a delivery does not record which of them paid in, so the shared string would
        // render "Confirming on NEAR" — the leg that delivered, not the one the user sent from.
        const isXchainDeposit = transfer.type === "xchain-deposit";
        const confirmedLabel = isXchainDeposit
          ? t("steps.confirmedXchain")
          : t("steps.confirmed", { sourceChain: transfer.sourceChain });
        const confirmingLabel = isXchainDeposit
          ? t("steps.confirmingXchain")
          : t("steps.confirming", { sourceChain: transfer.sourceChain });
        return (
          <>
            {hasStepFinished ? confirmedLabel : confirmingLabel}
            {renderTxLink(getSourceExplorerUrl())}
          </>
        );
      }
      case "bridging":
        return (
          <>
            {hasStepFinished ? t("steps.bridged") : t("steps.bridging")}
            {renderTxLink(
              hasStepFinished
                ? (getDestinationExplorerUrl() ?? getBridgeExplorerUrl())
                : getBridgeExplorerUrl(),
            )}
          </>
        );
      case "claimable":
        return (
          <>
            {t("steps.claimable")}
            {renderTxLink(getBridgeExplorerUrl())}
          </>
        );
      case "available":
        return t("steps.available");
      case "completed":
        // "Available in your account" is wrong for funds that left it — this is the one transfer
        // type whose last step happens somewhere Decibel cannot see.
        return transfer.type === "xchain-withdraw"
          ? t("steps.deliveredTo", { chain: transfer.destinationChainLabel })
          : t("steps.available");
    }
  };

  const getStepSubtitle = (row: TimelineRow): ReactNode | undefined => {
    const status = row.status;

    const confirmTimeEstimate = getConfirmationTimeEstimate(transfer.sourceChain);
    const bridgeTimeEstimate = transfer.estimatedBridgeTimeSeconds
      ? formatBridgeTimeRange(transfer.estimatedBridgeTimeSeconds)
      : t("subtitles.estimatedTimeLong");

    switch (row.step) {
      case "initiated":
        return undefined;
      case "withdrawal-queued": {
        if (status !== "in-progress") return undefined;
        const processed =
          isQueuedTransfer && "processedAmount" in transfer ? transfer.processedAmount : undefined;
        if (processed && processed > 0 && processed < transfer.amount) {
          return t("subtitles.withdrawalPartiallyProcessed", {
            processed: NumberFormatter.usdc(processed),
            total: NumberFormatter.usdc(transfer.amount),
          });
        }
        return t("subtitles.withdrawalQueuedInfo");
      }
      case "withdrawal-cancelled": {
        const reason =
          isQueuedTransfer && "cancelReason" in transfer ? transfer.cancelReason : undefined;
        const processed =
          isQueuedTransfer && "processedAmount" in transfer ? transfer.processedAmount : undefined;

        const reasonText = t(getCancelReasonI18nKey(reason));

        // Show partial fill amount so users know what they actually received.
        if (processed && processed > 0) {
          return t("subtitles.cancelledPartialFill", {
            processed: NumberFormatter.usdc(processed),
            reason: reasonText,
          });
        }
        return reasonText;
      }
      case "transfer-failed":
        // Withdrawals quote with `refundTo` set to the owner's own Aptos wallet, so there is a
        // refund to promise, and 1Click sometimes names a reason worth passing through. Deposits
        // have neither: the only thing upstream reports is the bare status `FAILED`, and a swap
        // that cannot complete retries rather than returning funds.
        if (transfer.type !== "xchain-withdraw") return t("subtitles.transferFailed");
        return transfer.failureReason
          ? t("subtitles.transferFailedWithReason", { reason: transfer.failureReason })
          : t("subtitles.transferFailedRefunded");
      case "confirming":
        return status === "pending" || status === "in-progress" ? confirmTimeEstimate : undefined;
      case "bridging":
        if (status !== "pending" && status !== "in-progress") return undefined;
        if (transfer.type === "cctp-deposit" && transfer.delayReason) {
          return t(FAST_DELAY_SUBTITLE[transfer.delayReason], { eta: bridgeTimeEstimate });
        }
        return bridgeTimeEstimate;
      case "claimable":
        if (status !== "in-progress") return undefined;
        return isAutoClaiming ? t("subtitles.autoClaimingInProgress") : t("subtitles.readyToClaim");
      case "available":
        // Sentinel from auto-import: cross-chain queued withdrawal where the real
        // destination chain is unknown. Let the user know they need to bridge manually.
        if (isSentinelQueuedXchainWithdraw(transfer)) {
          return t("subtitles.sentinelBridgeManually");
        }
        // A delivery the exchange cannot take as collateral is finished on arrival, so say so
        // rather than leaving the user waiting for a move that will never be offered. Gated on the
        // symbol the server reported: `assetSymbol` falls back to "USDC" for a card persisted
        // before that field existed, and "USDC cannot be used as collateral" is false.
        //
        // The `type` check is redundant with `settlement === "spot-only"` at runtime and is here
        // only so TS narrows far enough to reach `destinationSymbol` — dropping it breaks the
        // build rather than the behaviour, so leave it.
        if (
          settlement === "spot-only" &&
          transfer.type === "xchain-deposit" &&
          transfer.destinationSymbol
        ) {
          return t("subtitles.receivedInSpotOnly", {
            address: subaccountLabel ?? truncateAddress(destinationAccountAddress),
            symbol: assetSymbol,
          });
        }
        return t("subtitles.receivedIn", {
          address: subaccountLabel ?? truncateAddress(destinationAccountAddress),
        });
      case "completed":
        // Same reason as the step title above: nothing was received *in* anything Decibel holds.
        return transfer.type === "xchain-withdraw"
          ? t("subtitles.sentToAddress", {
              address: truncateAddress(destinationAccountAddress),
              chain: transfer.destinationChainLabel,
            })
          : t("subtitles.receivedIn", {
              address: subaccountLabel ?? truncateAddress(destinationAccountAddress),
            });
    }
  };

  const stepIconVariant = (row: TimelineRow): StepIconVariant => {
    if (row.step === "withdrawal-cancelled" || row.step === "transfer-failed") return "cancelled";
    // Last step shows as complete when reached
    if (row.isLast && (row.status === "complete" || row.status === "in-progress")) {
      return "complete";
    }
    if (row.status === "complete") return "complete";
    if (row.status === "in-progress") return "inProgress";
    return "pending";
  };

  const getTitle = () => {
    const amount = formatAmountWithUnit(transfer.amount);

    // Ahead of the generic withdraw branch, which hardcodes USDC and names no chain.
    if (transfer.type === "xchain-withdraw") {
      if (isFailed)
        return t("titleXchainWithdrawFailed", { chain: transfer.destinationChainLabel });
      return isComplete
        ? t("titleXchainWithdrawComplete", { amount, chain: transfer.destinationChainLabel })
        : t("titleXchainWithdrawInProgress", {
            completedSteps,
            totalSteps,
            chain: transfer.destinationChainLabel,
          });
    }

    if (isWithdrawTransfer(transfer)) {
      if (isCancelled) return t("titleWithdrawCancelled", { amount, asset: "USDC" });
      return isComplete
        ? t("titleWithdrawComplete", { amount, asset: "USDC" })
        : t("titleWithdrawInProgress", { completedSteps, totalSteps, asset: "USDC" });
    }

    if (transfer.type === "cctp-deposit") {
      return isComplete
        ? t("titleDepositComplete", { amount, asset: "USDC" })
        : t("titleDepositInProgress", { completedSteps, totalSteps, asset: "USDC" });
    }

    if (transfer.type === "xchain-deposit") {
      if (isFailed) return t("titleXchainDepositFailed");
      return isComplete
        ? t("titleDepositComplete", { amount, asset: assetSymbol })
        : t("titleXchainDepositInProgress", { completedSteps, totalSteps });
    }
    // mesh-deposit
    return isComplete
      ? t("titleMeshDepositComplete", { amount, asset: "USDC" })
      : t("titleMeshDepositInProgress", { completedSteps, totalSteps, asset: "USDC" });
  };

  return (
    <div
      data-testid="transfer-progress-card"
      className={cx(transferCardStyles({ highlighted: isHighlighted }), className)}
    >
      <div className={flex({ justify: "space-between", align: "center" })}>
        <h3 className={css({ textStyle: "heading.3xs", fontWeight: "medium" })}>{getTitle()}</h3>
        <div className={flex({ align: "center", gap: "4" })}>
          {isDismissible && (
            <Clickable onClick={() => removeTransfer(transfer.id)} aria-label={tGeneral("close")}>
              <IconX className={css({ w: "12", h: "12" })} />
            </Clickable>
          )}

          <CollapsibleIconButton
            collapsed={!isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
            ariaLabel={t("collapseAriaLabel")}
          />
        </div>
      </div>

      {typeof feeFormatted === "string" && (
        <p className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
          {t("bridgeFee", { fee: feeFormatted, asset: assetSymbol })}
        </p>
      )}
      {typeof feeFormatted === "string" && typeof feeUsd === "string" && (
        <p className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
          {t("bridgeFeeUsd", { usd: feeUsd })}
        </p>
      )}

      <div className={progressContainerStyles}>
        {rows.map((row) => {
          const status = row.status;
          const subtitle = getStepSubtitle(row);
          const isLast = row.isLast;
          const shouldShowStep = isExpanded ? true : transfer.currentStep === row.step;

          if (!shouldShowStep) return null;

          return (
            <StepRow
              key={row.step}
              data-testid={`transfer-step-${row.step}`}
              data-step-status={status}
              icon={<StepIcon variant={stepIconVariant(row)} />}
              connector={!isLast && isExpanded}
              title={getStepTitle(row)}
              subtitle={subtitle && <p className={stepSubtitleStyles}>{subtitle}</p>}
              muted={status === "pending"}
              trailing={!isExpanded && renderActionButton?.({ expanded: false })}
            />
          );
        })}
      </div>
      {isExpanded && renderActionButton?.({ expanded: true })}
      {footer && <div className={css({ mt: "auto" })}>{footer}</div>}
    </div>
  );
}
